import moment from "moment";

import { CalendarSlotV2, WorkDayV2 } from "@/Appointments/Models/Scheduling/WorkDayV2";
import { CalendarSlotType } from "@/Common/Models/UI/Enums";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { CalendarSlotCell } from "./CalendarSlotCell";

interface IAppointmentSchedulerV2 {
    workDays: Immutable.List<WorkDayV2>;
    minorAxisResolutionInMinutes?: number;
    selectedRulesetId?: number;
    onSelectRulesetOperation?: number;
    fullWidth: boolean;
    showScheduleDate?: boolean;
    onDivClick?: (calendarSlotModel: any) => void;
    deleteSinglePredefinedAppointmentOrRuleSetFunction?: (calendarSlot: CalendarSlotV2) => void;
    showXForDelete?: boolean;

    refresh?: () => void;
}

class SchedulingState {
    public workDays: WorkDayV2[];
    public NumberOfSubDivisionPerMajor: number;
    public minorAxisResolutionInMinutes: number;

    public static initState(props: IAppointmentSchedulerV2): SchedulingState {
        const newState = new SchedulingState();
        newState.minorAxisResolutionInMinutes = newState.isValidResolution(props.minorAxisResolutionInMinutes) ? props.minorAxisResolutionInMinutes : 15;

        if (props.workDays == null || props.workDays.count() == 0) {
            let emptyWD = new WorkDayV2();
            emptyWD.date = moment().toDate();
            emptyWD.calendarSlots = [];
            newState.workDays = [emptyWD];
        }
        else
            newState.workDays = props.workDays.toArray();

        newState.computeRowAndPositionForCalendarSlots();
        return newState;
    }

    private computeRowAndPositionForCalendarSlots(): void {
        this.NumberOfSubDivisionPerMajor = Math.round(60 / this.minorAxisResolutionInMinutes);
        // Major axis is always 1h to simplify calculations and positioning from Datetime starts
        // DV: If NumberOfSubDivision >= 6 (Resolution 5min or 10min), The 30min mark will be added inbetween 2 major axis

        this.workDays.forEach(x => x.calendarSlots.forEach(c => c.Init(this.minorAxisResolutionInMinutes, this.NumberOfSubDivisionPerMajor)));
        this.workDays = this.computePositionForCalendarSlots(this.workDays);
    }

    private computePositionForCalendarSlots(Workdays: WorkDayV2[]): WorkDayV2[] {
        Workdays.forEach(d => {
            let alreadyPositionedSlots: CalendarSlotV2[] = [];

            let collisionRanges: CalendarSlotV2[][] = []; // Detect Groups of Appointments that are next to each Others causing new columns splits

            let currentCollisionRange: CalendarSlotV2[] = [];
            let currentCollisionRangeEnd: Date;

            d.calendarSlots.forEach(c => {
                currentCollisionRange = currentCollisionRange.sortByPropertyDesc(i => i.Type === CalendarSlotType.RuleSet);
                currentCollisionRange = currentCollisionRange.sortByProperty(i => i.fromRaw);

                if (c.Type == CalendarSlotType.RuleSetSlot) { // RuleSet Are always on the LeftMost Column
                    c.IsPlaced = true;
                    alreadyPositionedSlots.push(c);
                }
                else {
                    c.IsPlaced = false;

                    if (currentCollisionRangeEnd != null && c.from >= currentCollisionRangeEnd) {
                        collisionRanges.push(currentCollisionRange);
                        currentCollisionRange = [];
                    }
                    currentCollisionRange.push(c);

                    if (currentCollisionRangeEnd == null || c.to > currentCollisionRangeEnd)
                        currentCollisionRangeEnd = c.to;
                }
            });

            if (currentCollisionRange.length > 0)
                collisionRanges.push(currentCollisionRange);

            collisionRanges.forEach(collisionRange => {
                let MaxPositionIndexZeroBased = 1;

                collisionRange.forEach(calendarSlotBeeingPositioned => {
                    let collisions = collisionRange
                        .filter(iteration => iteration.IsPlaced == true && iteration.from < calendarSlotBeeingPositioned.to && iteration.to > calendarSlotBeeingPositioned.from);

                    let ZeroBasedHorizontalPositionAttemp = 0;
                    while (!calendarSlotBeeingPositioned.IsPlaced) {
                        let isOccupied = collisions.filter(x => x.HorizontalPositionIndexZeroBase == ZeroBasedHorizontalPositionAttemp).length > 0;

                        if (!isOccupied) {
                            calendarSlotBeeingPositioned.HorizontalPositionIndexZeroBase = ZeroBasedHorizontalPositionAttemp;
                            calendarSlotBeeingPositioned.IsPlaced = true;
                            alreadyPositionedSlots.push(calendarSlotBeeingPositioned);

                            if ((ZeroBasedHorizontalPositionAttemp + 1) > MaxPositionIndexZeroBased) {
                                MaxPositionIndexZeroBased = (ZeroBasedHorizontalPositionAttemp + 1);
                            }
                        }

                        ZeroBasedHorizontalPositionAttemp++;
                    }
                });

                collisionRange.forEach(calendarSlotBeeingPositioned => {
                    calendarSlotBeeingPositioned.MaxCollisionsInCollisionRange = MaxPositionIndexZeroBased;
                });
            });

            d.calendarSlots = alreadyPositionedSlots;
        });

        return Workdays;
    }

    private isValidResolution(resolution: number): boolean {
        return resolution != null && resolution >= 5;
    }
}

class AppointmentSchedulerV2 extends React.Component<IAppointmentSchedulerV2, SchedulingState> {
    private tableBody: HTMLDivElement = null;

    constructor(props: IAppointmentSchedulerV2) {
        super(props);
        this.state = this.getInitialState(props);
    }

    public componentDidUpdate(prevProps: Readonly<IAppointmentSchedulerV2>): void {
        if (this.props !== prevProps) {
            if (this.props.workDays !== prevProps.workDays || this.props.minorAxisResolutionInMinutes != prevProps.minorAxisResolutionInMinutes) {
                this.setState(this.getInitialState(this.props));
            }
        }
    }

    public render(): JSX.Element {
        let workHours = this.getWorkHours();
        let majorRows: JSX.Element[] = [];

        workHours.forEach(hr => {
            majorRows.push(this.row(hr, 0, hr + "___key"));
        });

        return (
            <div className="basic-scheduler">
                <div className="sch-head-tb">
                    <table className="main">
                        {this.getColumnWidth(this.state.workDays.length)}
                        <thead>
                            <tr>
                                <th className="hrs" />
                                {this.state.workDays.map((wd: WorkDayV2, idx: number) => {
                                    let displayItemsCount = "";
                                    if (wd.EmployeeId != null)
                                        displayItemsCount = " ( " + (wd.calendarSlots != null && wd.calendarSlots.length != 0 ? wd.calendarSlots.length.toString() : "-") + " items )";

                                    let clinicianName = "";
                                    let clinician = cacheStore.GetCliniciansPersonsByContactFacetId(wd.EmployeeId);
                                    if (clinician != null)
                                        clinicianName = clinician.NameWithPrefix;

                                    if (this.props.showScheduleDate != null && this.props.showScheduleDate == true)
                                        return <th key={"th_" + idx}><span>{moment(wd.date).format("dddd")}, </span> <span>{moment(wd.date).format("DD MMMM YYYY")}</span></th>;
                                    else {
                                        return (
                                            <th key={"th_" + idx} style={{ paddingTop: "2px", paddingBottom: "2px" }}>
                                                <div className="flex-container">
                                                    <div className="flex-container" style={{ width: "100%", justifyContent: "center" }}>
                                                        <div style={{ paddingTop: "10px" }}>
                                                            <span>{clinicianName}</span><span style={{ marginLeft: "8px" }}>{displayItemsCount}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </th>
                                        );
                                    }
                                })}
                            </tr>
                        </thead>
                    </table>
                </div>
                <div className="sch-body-tb" ref={ref => this.tableBody = ref} style={{ height: this.tableBody != null ? window.innerHeight - this.tableBody.getBoundingClientRect().top : "100%" }}>
                    <table className="main">
                        {this.getColumnWidth(this.state.workDays.length)}
                        <tbody>
                            {majorRows}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private getInitialState(currentProps: IAppointmentSchedulerV2): SchedulingState {
        return SchedulingState.initState(currentProps);
    }

    private row(hr: number, min: number, key: string): JSX.Element {
        let minStr = min == 0 ? "00" : String(min);
        return (
            <tr key={key + "__dds"}>
                <td key="____hrstd" className="hrs">{hr + ":" + minStr}</td>
                {this.state.workDays.map((wd, idx) => (
                    <td key={idx + "___td"}>{this.subRow(hr, wd)}</td>
                ))}
            </tr>
        );
    }

    private subRow(hour: number, workDay: WorkDayV2): JSX.Element {
        let cdFrom = moment(workDay.date).set({ hour: hour, minute: 0, second: 0, millisecond: 0 });
        let cdTo = moment(cdFrom).add(1, "hour");

        let list = workDay.calendarSlots.filter(a => a.IsPlaced == true && moment(a.from) >= cdFrom && moment(a.from) < cdTo);

        return this.eventBlock(hour, list, workDay.HasRuleSet);
    }

    private eventBlock(hour: number, slots: CalendarSlotV2[], HasSideBar: boolean): JSX.Element {
        let SubRows: JSX.Element[] = [];
        for (let _i = 0; _i < this.state.NumberOfSubDivisionPerMajor; _i++) {
            let SlotsInIndex = slots.filter(x => x.minorAxisPositionIndexZeroBase == _i);
            if (SlotsInIndex.length == 0) { // The SubRow has no Appointments
                SubRows.push(
                    <tr key={"emptycell" + "_" + hour + "_" + _i}>
                        <td>-</td>
                    </tr>
                );
            }
            else { // The SubRow has Appointments
                SubRows.push(
                    <tr key={"slots" + "_" + hour + "_" + _i}>
                        <td className="m-event">
                            {
                                // for each appointments in the row
                                SlotsInIndex.map((slot, subSlotIndex) => (
                                    <CalendarSlotCell
                                        key={"slots" + "_" + hour + "_" + _i + "_" + subSlotIndex}
                                        slot={slot}
                                        index={_i}
                                        hasSideBar={HasSideBar}
                                        minorAxisResolutionInMinutes={this.state.minorAxisResolutionInMinutes}
                                        fullWidth={this.props.fullWidth}
                                        selectedRulesetId={this.props.selectedRulesetId}
                                        onClick={slotModel => this.props.onDivClick && this.props.onDivClick(slotModel)}
                                        showObjectDeleteX={!!this.props.deleteSinglePredefinedAppointmentOrRuleSetFunction}
                                        onDeleteSlotClick={slot => this.onDeleteSlotClick(slot)}
                                        onRulesetClick={slot => this.onRulesetClick(slot)}
                                    />
                                ))
                            }
                        </td>
                    </tr>
                );
            }
        }

        return (
            <table className="cell">
                <tbody>
                    {SubRows}
                </tbody>
            </table>
        );
    }

    private getColumnWidth(colNumber: number, includeTimeColumn: boolean = true): JSX.Element {
        if (colNumber === 0) return null;
        let percent = 100 / colNumber;

        let jsx: JSX.Element[] = includeTimeColumn ? [<col key="_d_a" style={{ width: "50px" }} />] : [];

        for (let i = 0; i < colNumber; i++) {
            jsx.push(<col key={i + "___dd"} style={{ width: percent + "%" }} />);
        }

        return (
            <colgroup>
                {jsx}
            </colgroup>
        );
    }

    private getWorkHours(): number[] {
        let min = 6;
        let max = 23;
        let output = [];
        for (let i = min; i < max + 1; i++) { output.push(i); }
        return output;
    }

    private onRulesetClick(slot: CalendarSlotV2): void {
        if (this.props.onSelectRulesetOperation != null) {
            let operation = new LocalEvent(this.props.onSelectRulesetOperation);
            operation.AddItem("value", slot);
            LocalEventDispatcher.dispatch(operation);
        }
    }

    private onDeleteSlotClick(slot: CalendarSlotV2): void {
        this.props.deleteSinglePredefinedAppointmentOrRuleSetFunction && this.props.deleteSinglePredefinedAppointmentOrRuleSetFunction(slot);
    }
}

export { AppointmentSchedulerV2 };
